/*
  1. To layout the items, we need to know the height of items => Use ItemMeasurer with ResizeObserver to track the height of items
  2. With the height of items, itemLayout will calculate the positions of items with the height, width and columns
  3. With the positions of items, we could place the items with absolute style
  4. With the absolute positions, we could do the virtualization by showing only the visible item via the scrollTop and offset.
*/

import React, { useState, useRef, useCallback, useLayoutEffect, useMemo, useEffect } from 'react';
import { List, ListItem } from '@fluentui/react-list-preview';
import { ResizeObserver as ResizeObserverPollyFill } from '@juggle/resize-observer';
import { debounce } from 'underscore';

import { ItemLayout } from './itemLayout';
import { ItemBox } from './itemBox';


const COLUMN_WIDTH = 240.5;
const ROW_HEIGHT = 200;
const PADDING = 24;

/*
The Masonry or Pinterest layout
*/

const layouts = {
  Mansory: 'Mansory',
  Grid: 'Grid',
};

interface itemProps {
  item: any,
}

interface GridProps {
  items: any,
  // layout?: string, // TODO: support GRID
  rowPadding?: number,
  columnPadding?: number,
  renderItem: () => JSX.Element,
  columnWidth?: number,
  rowHeight?: number,
  scrollContainer?: any,
  minItemHeight?: number,
  gridMode?: boolean,
  maxColumns?: number,
}

interface GridInternalProps {
  items: any [],
  columnWidth: number,
  rowHeight?: number,
  renderItem: (props: itemProps) => JSX.Element,
  itemLayout: ItemLayout,
  itemBox: ItemBox,
  itemBoxChanged: (cancel: any) => void,
  isItemBoxChanged: any,
  offset: any,
  scrollTop: any,
  windowRect: any,
  minItemHeight?: number,
  gridMode: boolean,
}

interface WrappedItemProps {
  item: any,
  columnWidth: number,
  rowHeight?: number,
  renderItem: (props: itemProps) => JSX.Element,
  itemLayout: ItemLayout,
  itemBox: ItemBox,
  itemBoxChanged: (cancel: any) => void,
  minItemHeight?: number,
  gridMode: boolean,
}

interface ItemMeasurerProps {
  item: any,
  itemBoxChanged: (cancel: any) => void,
  itemBox: ItemBox,
  renderItem: (props: itemProps) => JSX.Element,
  gridMode: boolean,
}

const ItemMeasurer = (props: ItemMeasurerProps) => {
  const {
    item,
    itemBoxChanged,
    itemBox,
    renderItem,
    gridMode,
  } = props;

  const [itemRef, setItemRef] = useState<Element | null>(null);
  const {
    _index: index,
  } = item;

  useEffect(() => {
    if (itemRef) {
      const cancel = { canceled: false };
      const box = itemRef.getBoundingClientRect();
      const { height: oldHeight, width: oldWidth } = itemBox.getBox(index);
      if (oldHeight !== box.height || oldWidth !== box.width) {
        if (box.height > 0 && box.width > 0) {
          itemBox.setBox(index, box.height, box.width);
          itemBoxChanged(cancel);
        }
      }
      return () => {
        cancel.canceled = true;
      };
    }
    return () => {};
  }, [itemBoxChanged, index, itemBox, itemRef]);

  useEffect(() => {
    if (itemRef) {
      // TODO: Batch the updates
      // TODO: Add a stop callback when the image is loaded
      // Use the Resize Observer to track the height changes of elements
      const cancel = { canceled: false };
      const updateViewPort = debounce((entries) => {
        if (cancel.canceled) { return; }
        if (entries[0] && entries[0].target === itemRef) {
          const {
            borderBoxSize,
          } = entries[0];

          const {
            blockSize,
            inlineSize,
          } = borderBoxSize[0];

          const { height: oldHeight, width: oldWidth } = itemBox.getBox(index);
          if (oldHeight !== blockSize || oldWidth !== inlineSize) {
            if (blockSize > 0 && inlineSize > 0) {
              itemBox.setBox(index, blockSize, inlineSize);
              itemBoxChanged(cancel);
            }
          }
        }
      }, 250);
      const resizeObserver = window.ResizeObserver ? new ResizeObserver(updateViewPort) : new ResizeObserverPollyFill(updateViewPort);
      resizeObserver.observe(itemRef);
      return () => {
        cancel.canceled = true;
        resizeObserver.unobserve(itemRef);
        resizeObserver.disconnect();
      };
    }
    return () => {};
  }, [itemBoxChanged, index, itemRef, itemBox]);

  return (
    <div
      ref={ref => setItemRef(ref)}
      style={{ width: '100%', height: (gridMode ? '100%' : undefined) }}
    >
      {renderItem({
        item,
      })}
    </div>);
};

const WrappedItem = (props: WrappedItemProps) => {
  // Get the positions and render the element absolutely
  // To get the positions, we need the height of the rendered elements
  const {
    item,
    columnWidth,
    rowHeight,
    gridMode,
    renderItem,
    itemBox,
    itemLayout,
    minItemHeight,
    itemBoxChanged,
  } = props;

  const { _index: index, tabIndex, ariaLabel } = item;
  const itemPosition = itemLayout.getItemPosition(index);
  const { height, width } = itemBox.getBox(index);
  const largerThanMinItemHeight = minItemHeight && height ? height > minItemHeight : true;

  // First, we will hide the element and get the height of rendered element.
  // After that,  we could calculate the positions of all the elements
  return (
    <ListItem
      className="list-item"
      id={`list-item-${index}`}
      aria-setsize={itemLayout.itemCount}
      aria-posinset={index + 1}
      aria-label={ariaLabel}
      style={itemPosition && (largerThanMinItemHeight) ? {
        width: columnWidth,
        height: gridMode ? rowHeight : undefined,
        position: 'absolute',
        writingMode: 'horizontal-tb',
        transform: `translate(${itemPosition.left}px, ${itemPosition.top}px)`, // same as top: itemPosition.top, left: itemPosition:left
        top: 0,
        left: 0,
      } : {
        width: columnWidth,
        height: gridMode ? rowHeight : undefined,
        zIndex: -1000,
        position: 'absolute',
        visibility: 'hidden',
        writingMode: 'horizontal-tb',
      }}
      tabIndex={tabIndex || 0}
      onAction={event => (item.onAction ? item.onAction(event) : null)}
      onKeyDown={event => (item.handleKeyDown ? item.handleKeyDown(event) : null)}
    >
      <ItemMeasurer
        itemBox={itemBox}
        item={item}
        gridMode={gridMode}
        renderItem={renderItem}
        itemBoxChanged={itemBoxChanged}
      />
    </ListItem>
  );
};

WrappedItem.defaultProps = {
  minItemHeight: undefined,
  rowHeight: undefined,
};

const GridInternal = (props: GridInternalProps) => {
  const {
    items,
    renderItem,
    columnWidth,
    rowHeight,
    gridMode,
    itemBoxChanged,
    itemBox,
    itemLayout,
    scrollTop,
    offset,
    windowRect,
    isItemBoxChanged,
    minItemHeight,
  } = props;

  // Filter out the elements that is not visible
  // TODO: use interval tree, if the performance is not acceptable, from now O(N) to O(logN)
  // TODO: use more specific height of the container but the windowHeight
  const visibleItems = items.filter((item) => {
    const { _index: index } = item;
    const itemPosition = itemLayout.getItemPosition(index);
    let isVisible = true;
    if (itemPosition) {
      const { height } = itemBox.getBox(index);
      if (height) {
        const { top: itemTop } = itemPosition;
        const itemBottom = itemPosition.top + height;
        const {
          height: windowHeight,
        } = windowRect;
        const bufferHeight = windowHeight / 2;
        const topBorder = Math.max(0, scrollTop - offset - bufferHeight);
        const bottomBorder = (scrollTop - offset) + windowHeight + windowHeight;

        isVisible = !(itemTop > bottomBorder || itemBottom < topBorder);
      }
    }
    return isVisible;
  });

  const [render, forceRender] = useState(isItemBoxChanged);

  useEffect(() => {
    forceRender(isItemBoxChanged);
  }, [isItemBoxChanged]);

  return (
    <div style={{ position: 'relative', height: `${itemLayout.getMaxHeight()}px` }}>
      <List navigationMode="composite" role="list">
        {
          visibleItems.map((item) => {
            const {
              _index: index,
            } = item;

            return (
              <WrappedItem
                item={item}
                key={`key_${index}`} // Add key to identify the item is changed or the resizeObserver would not work and track the wrong element
                renderItem={renderItem}
                itemLayout={itemLayout}
                itemBox={itemBox}
                minItemHeight={minItemHeight}
                columnWidth={columnWidth}
                gridMode={gridMode || false}
                rowHeight={rowHeight}
                itemBoxChanged={itemBoxChanged}
              />);
          })
        }
      </List>
    </div>
  );
};

GridInternal.defaultProps = {
  minItemHeight: undefined,
  rowHeight: undefined,
};


const useWindow = () => {
  const getSize = useCallback(() => {
    const {
      clientWidth,
      clientHeight,
    } = document.documentElement;

    return {
      width: clientWidth,
      height: clientHeight,
    };
  }, []);

  const [windowRect, setWindowRect] = useState(getSize());

  const [update, setUpdate] = useState({
    ...windowRect,
  });

  useLayoutEffect(() => {
    const updateSize = () => {
      setUpdate(getSize());
    };
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [getSize]);

  useLayoutEffect(() => {
    // TODO_opt: if this updates are too frequently, we could do optimization
    // TODO: debounce
    setWindowRect(update);
  }, [update]);

  return [windowRect];
};

const useTopDivMetaData = (topDiv, topDivRect) => {
  const [offset, setOffset] =
    useState(0);

  useLayoutEffect(() => {
    if (topDiv) {
      let target = topDiv;
      let newOffset = 0;
      do {
        newOffset += target.offsetTop;
        target = target.offsetParent;
      } while (target);

      if (newOffset !== offset) {
        setOffset(newOffset);
      }
    }
  }, [offset, topDiv, topDivRect]);

  return [offset];
};

const useScroll = (scrollContainer) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [update, setUpdate] = useState({
    scrollY: scrollTop,
  });

  useLayoutEffect(() => {
    const scrollHandler = (e) => {
      const scrollTop = e && e.target.scrollTop;
      setUpdate({
        scrollY: scrollTop,
      });
    };
    const windowScrollHandler = () => {
      const {
        scrollY,
      } = window;
      setUpdate({
        scrollY,
      });
    };
    if (scrollContainer) {
      const element = (typeof (scrollContainer)) === 'string' ? document.querySelector(scrollContainer) : scrollContainer;
      if (element) {
        element.addEventListener('scroll', scrollHandler);
      }
    } else {
      window.addEventListener('scroll', windowScrollHandler);
    }
    return () => {
      if (scrollContainer) {
        const element = (typeof (scrollContainer)) === 'string' ? document.querySelector(scrollContainer) : scrollContainer;
        if (element) {
          element.removeEventListener('scroll', scrollHandler);
        }
      } else {
        window.removeEventListener('scroll', windowScrollHandler);
      }
    };
  }, [scrollContainer]);

  useLayoutEffect(() => {
    // TODO_opt: if this updates are too frequently, we could do optimization
    const {
      scrollY,
    } = update;
    setScrollTop(scrollY);
  }, [update]);

  return [scrollTop];
};

export const Grid = (props: GridProps) => {
  const {
    items,
    renderItem,
    rowPadding,
    columnPadding,
    columnWidth: columnWidthProp,
    rowHeight: rowHeightProp,
    scrollContainer,
    minItemHeight,
    gridMode,
    maxColumns,
  } = props;

  const [topDiv, setTopDiv] = useState<Element | null>(null);
  const [topDivRect, setTopDivRect] = useState({
    clientHeight: 0,
    clientWidth: 0,
  });
  const [columnWidth] = useState(columnWidthProp || COLUMN_WIDTH);
  const [rowHeight] = useState(rowHeightProp || ROW_HEIGHT);
  const [columnCount, setColumnCount] = useState(1);
  const [isItemBoxChanged, setItemBoxChanged] = useState(0);
  const [isItemLengthChanged, setItemLengthChanged] = useState(0);
  const count = useRef(0);

  // Two components, itemBox and itemLayout.
  // The itemBox track the height, width of each item
  // With the heights of each item, the itemLayout computes the positions of the items
  const itemBox = useRef(new ItemBox());
  const itemLayout = useRef(new ItemLayout({
    columnCount,
    columnWidth,
    columnPadding,
    rowPadding,
    itemBox: itemBox.current,
    itemCount: items.length,
  }));

  // The distance of the top offset
  const [offset] = useTopDivMetaData(topDiv, topDivRect);

  const wrappedScrollContainer = useMemo(() => {
    if ((typeof (scrollContainer)) === 'string') {
      return scrollContainer;
    }
    if (scrollContainer && scrollContainer.current) {
      return scrollContainer.current;
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainer, scrollContainer.current]);

  // The distance of the global scroll top
  const [scrollTop] = useScroll(wrappedScrollContainer);
  // The window view
  const [windowRect] = useWindow();

  const itemBoxChanged = useCallback(debounce((cancel) => {
    // React will accumulate the update
    if (cancel && cancel.canceled) {
      return;
    }

    setItemBoxChanged(isItemBoxChanged => isItemBoxChanged + 1);
  }, 250), [setItemBoxChanged]);

  const itemLengthChanged = useCallback(() => {
    // React will accumulate the update
    setItemLengthChanged(isItemLengthChanged => isItemLengthChanged + 1);
  }, []);

  useEffect(() => {
    if (topDiv) {
      const {
        clientHeight,
        clientWidth,
      } = topDiv;

      setTopDivRect({
        clientHeight,
        clientWidth,
      });
    }
  }, [topDiv]);

  useEffect(() => {
    if (topDiv) {
      const cancel = { canceled: false };
      const updateTopDivRect = debounce((entries) => {
        if (entries[0] && cancel.canceled === false) {
          const {
            clientHeight,
            clientWidth,
          } = entries[0].target;

          setTopDivRect({
            clientHeight,
            clientWidth,
          });
        }
      }, 250);
      const resizeObserver = window.ResizeObserver ? new ResizeObserver(updateTopDivRect) : new ResizeObserverPollyFill(updateTopDivRect);
      resizeObserver.observe(topDiv);
      return () => {
        cancel.canceled = true;
        resizeObserver.unobserve(topDiv);
        resizeObserver.disconnect();
      };
    }
    return () => {};
  }, [topDiv]);

  const calculatedColumnWidth = useCallback((availableColumn, items) => {
    const {
      clientWidth,
    } = topDivRect;
    const extendedColumnWidth = (clientWidth - ((columnPadding || PADDING) * (availableColumn - 1))) / availableColumn;
    return (items.length >= availableColumn ? extendedColumnWidth : columnWidth);
  }, [columnPadding, columnWidth, topDivRect]);

  const calculatedRowHeight = useCallback((availableColumn, items) => {
    const targetColumnWidth = calculatedColumnWidth(availableColumn, items);
    const targetRowHeight = (targetColumnWidth / columnWidth) * rowHeight;
    return targetRowHeight;
  }, [calculatedColumnWidth, columnWidth, rowHeight]);

  useEffect(() => {
    if (topDivRect) {
      const {
        clientWidth,
      } = topDivRect;
      const columnWithPadding = columnWidth + (columnPadding || PADDING);
      let availableColumn;
      if (clientWidth > 0) {
        if (clientWidth <= columnWidth) {
          availableColumn = 1;
        } else {
          availableColumn = 1 + Math.floor((clientWidth - columnWidth) / columnWithPadding);
        }
        if (maxColumns) {
          availableColumn = Math.min(availableColumn, maxColumns);
        }
        const targetColumnWidth = calculatedColumnWidth(availableColumn, items);
        if (availableColumn !== columnCount) {
          setColumnCount(availableColumn);
        }
        if (availableColumn !== columnCount
          || targetColumnWidth !== itemLayout.current.columnWidth) {
          // for new layout, we would need to place all the items
          itemLayout.current.updateLayout({
            columnCount: availableColumn,
            columnWidth: targetColumnWidth,
            columnPadding,
            rowPadding,
            itemBox: itemBox.current,
            itemCount: items.length,
          });
        }
      }
    }
  }, [calculatedColumnWidth, columnCount, columnPadding, columnWidth, items, items.length, maxColumns, rowPadding, topDivRect]);

  useEffect(() => {
    itemLayout.current.setItemCount(items.length);
    itemLengthChanged();
  }, [itemLengthChanged, items.length]);

  useEffect(() => {
    // for force update, we would only need to update the changed items
    count.current += 1;
    itemLayout.current.placeItems();
  }, [isItemBoxChanged]);

  const indexedItems = useMemo(() => items.map((item, index) => ({
    ...item,
    _index: index,
  })), [items]);

  return (
    <div
      className="waterfall-grid"
      style={{ width: '100%' }}
      ref={ref => setTopDiv(ref)}
    >
      <GridInternal
        items={indexedItems}
        columnWidth={calculatedColumnWidth(columnCount, items)}
        rowHeight={gridMode ? calculatedRowHeight(columnCount, items) : undefined}
        gridMode={gridMode || false}
        renderItem={renderItem}
        itemLayout={itemLayout.current}
        itemBox={itemBox.current}
        itemBoxChanged={itemBoxChanged}
        isItemBoxChanged={isItemBoxChanged}
        minItemHeight={minItemHeight}
        offset={offset}
        scrollTop={scrollTop}
        windowRect={windowRect}
      />
    </div>
  );
};

Grid.defaultProps = {
  columnPadding: 24,
  rowPadding: 16,
  columnWidth: COLUMN_WIDTH,
  rowHeight: ROW_HEIGHT,
  scrollContainer: {},
  minItemHeight: undefined,
  gridMode: false,
  maxColumns: undefined,
};
